import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { AdminPageHeader, Card, Spinner, ErrorBanner, Select } from "./_shared";

export const Analytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    adminApi.get("/admin/analytics", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load analytics."));
  }, [days]);

  return (
    <div className="space-y-5" data-testid="analytics-page">
      <AdminPageHeader
        eyebrow="ANALYTICS"
        title="What Kerala is saying"
        sub="DB-only telemetry: volume by day, top categories, demographics."
        action={
          <Select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="w-40" data-testid="analytics-range">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </Select>
        }
      />

      {err && <ErrorBanner message={err} />}
      {!data && !err && <Spinner />}

      {data && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <BarSeries title="Messages per day" data={data.messages_by_day} color="#FF9933" testId="chart-messages" />
            <BarSeries title="Upvotes per day" data={data.upvotes_by_day} color="#138808" testId="chart-upvotes" />
          </div>

          <Card title="By category" testId="chart-categories">
            <BarTable rows={(data.by_category || []).map((r) => ({ label: r.category, value: r.count, sub: `${r.upvotes} upvotes` }))} />
          </Card>

          <div className="grid md:grid-cols-3 gap-4">
            <Card title="By district" testId="chart-districts">
              <BarTable rows={(data.by_district || []).map((r) => ({ label: r.district, value: r.count }))} compact />
            </Card>
            <Card title="By age" testId="chart-age">
              <BarTable rows={(data.by_age || []).map((r) => ({ label: r.age_group, value: r.count }))} compact />
            </Card>
            <Card title="By occupation" testId="chart-occupation">
              <BarTable rows={(data.by_occupation || []).map((r) => ({ label: r.occupation, value: r.count }))} compact />
            </Card>
          </div>

          <Card title="Top 10 messages" testId="top-messages">
            <ol className="space-y-2">
              {(data.top_messages || []).map((m, i) => (
                <li key={m.id} className="flex gap-3 text-sm" data-testid={`top-msg-${i}`}>
                  <span className="font-display text-xl w-7 shrink-0" style={{ color: "#FF9933" }}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="leading-snug">{m.message}</p>
                    <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500 mt-1">{m.category} · {m.upvotes} upvotes</p>
                  </div>
                </li>
              ))}
              {(data.top_messages || []).length === 0 && <p className="text-sm text-neutral-500">No data yet.</p>}
            </ol>
          </Card>
        </>
      )}
    </div>
  );
};

const BarSeries = ({ title, data, color, testId }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <Card title={title} testId={testId}>
      {(!data || data.length === 0) ? (
        <p className="text-sm text-neutral-500">No data in range.</p>
      ) : (
        <div className="flex items-end gap-1 h-32">
          {data.slice(-30).map((d) => (
            <div key={d.date} className="flex-1 flex flex-col justify-end" title={`${d.date}: ${d.count}`}>
              <div style={{ height: `${(d.count / max) * 100}%`, background: color, minHeight: 2 }} />
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500 mt-2">
        Max in range: {max}
      </p>
    </Card>
  );
};

const BarTable = ({ rows, compact = false }) => {
  if (!rows || rows.length === 0) return <p className="text-sm text-neutral-500">No data.</p>;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-2">
      {rows.slice(0, compact ? 10 : 20).map((r) => (
        <li key={r.label}>
          <div className="flex items-baseline justify-between text-xs">
            <span className="uppercase font-bold tracking-[0.2em]">{r.label}</span>
            <span className="font-display text-base" style={{ color: "#FF9933" }}>
              {r.value} {r.sub && <span className="text-[10px] text-neutral-500 ml-1">({r.sub})</span>}
            </span>
          </div>
          <div className="h-1.5 mt-1" style={{ background: "#1f1f1f" }}>
            <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: "#FF9933" }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

export default Analytics;
